import {AsyncStatus} from 'enums';
import {IDeviceModel, IDeviceModelAPI, IManufacturer, IManufacturerAPI} from 'interfaces';
// Not using index import for services to avoid circular dependency
import {axiosAPI} from './API';

interface IDeviceModelServer {
    id: number,
    name: string,
    manufacturerId: number,
    manufacturerName: string,
}

interface IManufacturersModelsResp {
    manufacturers: IManufacturer[],
    models: IDeviceModel[],
}

// Singleton for interacting with manufacturers
class _ManufacturersService {

    public manufacturers: IManufacturer[] = [];
    public manufacturersMap: {[id: number]: IManufacturer} = {};

    public models: IDeviceModel[] = [];
    public modelsMap: {[id: number]: IDeviceModel} = {};

    public getManufacturers(): Promise<IManufacturer[]> {
        return axiosAPI.get<IManufacturer[]>('/manufacturers')
            .then((resp) => {
                let manufacturersResponse = resp.data;
                this.manufacturers = manufacturersResponse;
                manufacturersResponse.forEach((g) => this.manufacturersMap[g.id] = g);
                this.manufacturers = this.manufacturers.filter(g => !g.isDeleted);
                const collator = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});
                this.manufacturers = this.manufacturers.sort((a, b) => collator.compare(a.name, b.name));
                return manufacturersResponse;
            })
    }

    public postManufacturer(man: IManufacturerAPI): Promise<IManufacturer> {
        return axiosAPI.post<IManufacturer>('/manufacturers', man)
            .then((resp) => {
                let manufacturer = resp.data;
                this.manufacturers.push(manufacturer);
                this.manufacturersMap[manufacturer.id] = manufacturer;
                const collator = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});
                this.manufacturers = this.manufacturers.sort((a, b) => collator.compare(a.name, b.name));
                return resp.data;
            })
            .catch((err) => {
                console.error("Error posting manufacturer: ", err);
                throw err;
            });
    }

    public deleteManufacturer(id: number): Promise<any> {
        return axiosAPI.delete<any>(`/manufacturers/${id}`)
            .then(() => {
                // remove from list
                this.manufacturers = this.manufacturers.filter(g => g.id != id);
                this.manufacturersMap[id].isDeleted = true;
                return;
            })
            .catch((err) => {
                console.error("Error deleting manufacturer: ", err);
                throw err;
            });
    }

    public getModels(): Promise<IDeviceModel[]> {
        return axiosAPI.get<IDeviceModel[]>('/models')
            .then((resp) => {
                let modelsResp = resp.data;
                this.models = modelsResp;
                modelsResp.forEach((m) => this.modelsMap[m.id] = m);
                this.models = this.models.filter(m => !m.isDeleted);
                const collator = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});
                this.models = this.models.sort((a, b) => collator.compare(a.name, b.name));
                return modelsResp;
            })
            .catch((err) => {
                console.error('Error getting models: ', err);
                throw err;
            });
    }

    public postModel(model: IDeviceModelAPI): Promise<IDeviceModel> {
        return axiosAPI.post<IDeviceModel>('/models', model)
            .then((resp) => {
                let model = resp.data;
                // response doesnt return manufacturer name
                model.manufacturerName = this.manufacturersMap[model.manufacturerId].name;
                this.models.push(model);
                const collator = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});
                this.models = this.models.sort((a, b) => collator.compare(a.name, b.name));
                this.modelsMap[model.id] = model;
                return resp.data;
            })
            .catch((err) => {
                console.error("Error posting model: ", err);
                throw err;
            });
    }

    public deleteModel(id: number): Promise<any> {
        return axiosAPI.delete<any>(`/models/${id}`)
            .then(() => {
                // remove from list
                this.models = this.models.filter(m => m.id != id);
                this.modelsMap[id].isDeleted = true;
                return;
            })
            .catch((err) => {
                console.error("Error deleting model: ", err);
                throw err;
            });
    }
}

export const ManufacturersService = new _ManufacturersService();